/**
 * The founder's market assumptions — ONE definition, both runtimes.
 *
 * There were three, and they disagreed in a way that made the feature
 * unusable. The editor sent `serviceableCustomerUnits` and `totalCustomerUnits`
 * and no `founderSourceUrls`; the server's strict schema required
 * `customerUnits` and `founderSourceUrls` and recognised neither of the two the
 * editor sent; the client type declared a fourth arrangement. Every real save
 * was rejected as an invalid request body, and the tests passed because each
 * side was exercised against a fixture written to match itself.
 *
 * So the shape lives here, with no imports, and the server's Zod schema
 * delegates to `parseMarketAssumptions` rather than restating it. Deno reads
 * this file directly; Metro and Jest read it through `@/lib/strategic-analysis`.
 *
 * NAMES CARRY THE SCOPE. `customerUnits` was ambiguous — it is the field the
 * bottom-up model multiplies, and the model needs to know whether it was handed
 * every potential customer or only the serviceable ones, because those produce
 * TAM and SAM respectively. The three scopes are now named for what they are and
 * the ambiguous name is gone:
 *
 *   totalCustomerUnits      -> TAM scope. Optional; absent means TAM is not computed.
 *   serviceableCustomerUnits-> SAM scope. Required.
 *   reachableShare          -> SOM scope, as a share of SAM. Required, 0..1.
 *
 * SOURCE URLS ARE OPTIONAL, AND THAT IS DELIBERATE. These are the founder's own
 * estimates about their own company; requiring a citation for them was a rule
 * nobody could satisfy and it blocked every save. They are normalized to an
 * array so nothing downstream has to distinguish absent from empty.
 */

export interface MarketRange {
  low: number;
  base: number;
  high: number;
}

export interface MarketAssumptions {
  /** ISO code, upper case. Compared against the currency of any published base. */
  currency: string;
  baseYear: number;
  /** What counts as ONE customer. Every count below is expressed in this unit. */
  customerUnit: string;
  /** The market as the founder defines it, for the definition comparison. */
  marketDefinition?: string;
  /** Where. Compared against the geography of any published base figure. */
  geography?: string;
  useCase?: string;

  /** TAM scope. Absent means TAM is not computed rather than assumed. */
  totalCustomerUnits?: MarketRange;
  /** SAM scope. */
  serviceableCustomerUnits: MarketRange;
  /** SOM scope, as a share of SAM. */
  reachableShare: MarketRange;
  annualValuePerCustomer: MarketRange;

  /** The founder's own headline figures. A COMPARISON, never an input. */
  founderTam?: MarketRange;
  founderSam?: MarketRange;
  founderSom?: MarketRange;
  /** Normalized to an array. Optional by nature — these are the founder's own. */
  founderSourceUrls: string[];
}

/** Every key the contract recognises. Anything else is an error, not extra data. */
export const MARKET_ASSUMPTION_KEYS: readonly string[] = [
  'currency',
  'baseYear',
  'customerUnit',
  'marketDefinition',
  'geography',
  'useCase',
  'totalCustomerUnits',
  'serviceableCustomerUnits',
  'reachableShare',
  'annualValuePerCustomer',
  'founderTam',
  'founderSam',
  'founderSom',
  'founderSourceUrls',
];

export type MarketAssumptionsParse =
  | { ok: true; value: MarketAssumptions }
  | { ok: false; issues: string[] };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readRange(value: unknown, path: string, issues: string[]): MarketRange | null {
  if (!isRecord(value)) {
    issues.push(`${path}: expected an object with low, base and high`);
    return null;
  }
  for (const key of Object.keys(value)) {
    if (!['low', 'base', 'high'].includes(key)) issues.push(`${path}.${key}: unrecognized key`);
  }
  const out: Partial<MarketRange> = {};
  for (const bound of ['low', 'base', 'high'] as const) {
    const raw = value[bound];
    if (typeof raw !== 'number' || !Number.isFinite(raw)) {
      issues.push(`${path}.${bound}: expected a finite number`);
      continue;
    }
    out[bound] = raw;
  }
  if (out.low === undefined || out.base === undefined || out.high === undefined) return null;
  if (!(out.low <= out.base && out.base <= out.high)) {
    issues.push(`${path}: bounds must be ordered low <= base <= high`);
    return null;
  }
  return { low: out.low, base: out.base, high: out.high };
}

function readOptionalRange(
  value: unknown,
  path: string,
  issues: string[],
): MarketRange | undefined | null {
  if (value === undefined || value === null) return undefined;
  return readRange(value, path, issues);
}

function readOptionalText(value: unknown, path: string, issues: string[]): string | undefined | null {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'string') {
    issues.push(`${path}: expected a string`);
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}

/**
 * Parse and NORMALIZE. The value this returns is what gets persisted, so the
 * normalization is part of the contract: currency upper-cased, text trimmed,
 * blank optional text dropped rather than stored as `''`, and
 * `founderSourceUrls` always an array.
 */
export function parseMarketAssumptions(input: unknown): MarketAssumptionsParse {
  const issues: string[] = [];
  if (!isRecord(input)) return { ok: false, issues: ['assumptions: expected an object'] };

  for (const key of Object.keys(input)) {
    if (!MARKET_ASSUMPTION_KEYS.includes(key)) issues.push(`assumptions.${key}: unrecognized key`);
  }

  const currencyRaw = input.currency;
  let currency = '';
  if (typeof currencyRaw !== 'string' || currencyRaw.trim().length === 0) {
    issues.push('assumptions.currency: expected a non-empty ISO currency code');
  } else {
    currency = currencyRaw.trim().toUpperCase();
  }

  const baseYearRaw = input.baseYear;
  let baseYear = 0;
  if (
    typeof baseYearRaw !== 'number' || !Number.isInteger(baseYearRaw) ||
    baseYearRaw < 1900 || baseYearRaw > 2200
  ) {
    issues.push('assumptions.baseYear: expected a four-digit year between 1900 and 2200');
  } else {
    baseYear = baseYearRaw;
  }

  const customerUnitRaw = input.customerUnit;
  let customerUnit = '';
  if (typeof customerUnitRaw !== 'string' || customerUnitRaw.trim().length === 0) {
    issues.push('assumptions.customerUnit: expected a non-empty customer unit');
  } else {
    customerUnit = customerUnitRaw.trim();
  }

  const marketDefinition = readOptionalText(input.marketDefinition, 'assumptions.marketDefinition', issues);
  const geography = readOptionalText(input.geography, 'assumptions.geography', issues);
  const useCase = readOptionalText(input.useCase, 'assumptions.useCase', issues);

  const serviceableCustomerUnits = readRange(
    input.serviceableCustomerUnits,
    'assumptions.serviceableCustomerUnits',
    issues,
  );
  const annualValuePerCustomer = readRange(
    input.annualValuePerCustomer,
    'assumptions.annualValuePerCustomer',
    issues,
  );
  const reachableShare = readRange(input.reachableShare, 'assumptions.reachableShare', issues);
  const totalCustomerUnits = readOptionalRange(
    input.totalCustomerUnits,
    'assumptions.totalCustomerUnits',
    issues,
  );
  const founderTam = readOptionalRange(input.founderTam, 'assumptions.founderTam', issues);
  const founderSam = readOptionalRange(input.founderSam, 'assumptions.founderSam', issues);
  const founderSom = readOptionalRange(input.founderSom, 'assumptions.founderSom', issues);

  // A share is a share. Above 1 it is a percentage somebody forgot to divide,
  // and multiplying SAM by 30 rather than by 0.3 produces a SOM larger than the
  // market it came from.
  if (reachableShare && (reachableShare.low < 0 || reachableShare.high > 1)) {
    issues.push('assumptions.reachableShare: must be a decimal share between 0 and 1');
  }
  // A count cannot be negative, and a zero-high count makes every derived figure
  // zero without saying so.
  for (
    const [label, value] of [
      ['serviceableCustomerUnits', serviceableCustomerUnits],
      ['annualValuePerCustomer', annualValuePerCustomer],
      ['totalCustomerUnits', totalCustomerUnits],
    ] as const
  ) {
    if (value && value.low < 0) issues.push(`assumptions.${label}: must not be negative`);
  }
  // SAM cannot exceed TAM. The scopes are nested by definition, and a serviceable
  // count larger than the total one means the two were measured differently.
  if (
    totalCustomerUnits && serviceableCustomerUnits &&
    serviceableCustomerUnits.base > totalCustomerUnits.base
  ) {
    issues.push(
      'assumptions.serviceableCustomerUnits: cannot exceed the total customer count it is a subset of',
    );
  }

  let founderSourceUrls: string[] = [];
  const urlsRaw = input.founderSourceUrls;
  if (urlsRaw !== undefined && urlsRaw !== null) {
    if (!Array.isArray(urlsRaw)) {
      issues.push('assumptions.founderSourceUrls: expected an array of URLs');
    } else {
      for (const [index, raw] of urlsRaw.entries()) {
        if (typeof raw !== 'string' || !isHttpUrl(raw)) {
          issues.push(`assumptions.founderSourceUrls.${index}: expected an http(s) URL`);
          continue;
        }
        founderSourceUrls.push(raw.trim());
      }
    }
  }
  founderSourceUrls = [...new Set(founderSourceUrls)];

  if (
    issues.length > 0 || serviceableCustomerUnits === null || annualValuePerCustomer === null ||
    reachableShare === null || totalCustomerUnits === null || founderTam === null ||
    founderSam === null || founderSom === null || marketDefinition === null ||
    geography === null || useCase === null
  ) {
    return { ok: false, issues: issues.length > 0 ? issues : ['assumptions: could not be parsed'] };
  }

  return {
    ok: true,
    value: {
      currency,
      baseYear,
      customerUnit,
      ...(marketDefinition === undefined ? {} : { marketDefinition }),
      ...(geography === undefined ? {} : { geography }),
      ...(useCase === undefined ? {} : { useCase }),
      ...(totalCustomerUnits === undefined ? {} : { totalCustomerUnits }),
      serviceableCustomerUnits,
      reachableShare,
      annualValuePerCustomer,
      ...(founderTam === undefined ? {} : { founderTam }),
      ...(founderSam === undefined ? {} : { founderSam }),
      ...(founderSom === undefined ? {} : { founderSom }),
      founderSourceUrls,
    },
  };
}

/**
 * `new URL()` without depending on it being present.
 *
 * Deno and Hermes both have it, but this module promises zero imports and zero
 * host assumptions, and a scheme check is all the contract needs: the point is
 * to refuse `javascript:` and `data:`, not to validate an authority component.
 */
function isHttpUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!/^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(trimmed)) return false;
  return true;
}

/**
 * Which fields the market methodology needs before it can compare a founder
 * estimate to a researched one.
 *
 * Returned as keys rather than a boolean so the readiness response can name
 * exactly what is missing — "market assumptions are incomplete" is not something
 * a founder can act on.
 */
export function missingMarketAssumptionFields(input: unknown): string[] {
  const parsed = parseMarketAssumptions(input);
  if (parsed.ok) return [];
  const missing = new Set<string>();
  for (const issue of parsed.issues) {
    const field = issue.split(':')[0]?.replace(/^assumptions\./, '').split('.')[0];
    if (field) missing.add(field);
  }
  return [...missing].sort();
}

/**
 * Read a STORED assumptions row.
 *
 * Separate from `parseMarketAssumptions` on purpose, and asymmetric on purpose:
 * this accepts a shape a previous server schema would have written and the
 * client submission path does not. `customerUnits` was the name the original
 * `marketAssumptionsSchema` required — no editor ever sent it, so a row carrying
 * it can only have come from a hand-written insert or a script, but a stored row
 * this code refuses to read is a market analysis that silently loses its founder
 * inputs.
 *
 * Reopening the legacy key to NEW submissions would be the mistake: the ambiguity
 * it caused — is this every potential customer, or only the serviceable ones? —
 * is exactly what the scope-named fields exist to remove. Accepting it here and
 * refusing it there is the whole point of having two functions.
 */
export function parseStoredMarketAssumptions(input: unknown): MarketAssumptionsParse {
  if (!isRecord(input)) return { ok: false, issues: ['assumptions: expected an object'] };

  // Try the canonical shape first. Nearly every row is one.
  const direct = parseMarketAssumptions(input);
  if (direct.ok) return direct;

  if (!('customerUnits' in input) || 'serviceableCustomerUnits' in input) return direct;

  const { customerUnits, ...rest } = input;
  const migrated = { ...rest, serviceableCustomerUnits: customerUnits };
  const parsed = parseMarketAssumptions(migrated);
  // The migrated read only wins when it actually succeeds. A row that fails both
  // ways reports the CANONICAL failure, because that is the shape a reader is
  // being asked to fix.
  return parsed.ok ? parsed : direct;
}
